import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Fragment, useEffect } from 'react';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { VOICE_QA_VERDICT_BADGES } from '@apps/web/components/voice_calls/voice_qa_badges';
import { cn } from '@apps/web/lib/utils';
import {
    formatLatencyMs,
    formatUsd,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import {
    booleanParam,
    shadowLaneSearchSchema,
} from '@apps/web/routes/_app/admin/voice-qa/search_params';
import {
    adjudicationLabel,
    useShadowComparisonUiEnabled,
} from '@apps/web/routes/_app/admin/voice-qa/shadow_comparison_ui';
import {
    useVoiceQAShadowComparison,
    useVoiceQAShadowStats,
} from '@apps/web/swr_data_fetching/admin_voice_qa_queries';
import {
    type RatingLabel,
    type VoiceQAShadowAdjudicationCountRead,
    type VoiceQAShadowConfusionCell,
    type VoiceQAShadowCostRead,
    type VoiceQAShadowLatencyRead,
    type VoiceQAShadowModelStatsRead,
    type VoiceQAShadowPairRead,
    type VoiceQAShadowStatsResponse,
} from '@libraries/typescript/api_client/schema_types';

function adjudicationBadgeClassName(whoWasRight: string): string {
    if (whoWasRight === 'glm') {
        return 'text-green-700';
    }
    if (whoWasRight === 'gemini') {
        return 'text-blue-700';
    }
    return 'text-muted-foreground';
}

const SINCE_DAYS_OPTIONS: ReadonlyArray<number> = [7, 14, 30, 90];
const PAGE_SIZE = 50;
const ALL_MODELS_VALUE = '__all__';

const shadowComparisonSearchSchema = shadowLaneSearchSchema.extend({
    since_days: z.coerce
        .number()
        .refine((value) => SINCE_DAYS_OPTIONS.includes(value))
        .optional()
        .default(7),
    disagreements_only: booleanParam.default(false),
    page: z.coerce.number().min(1).optional().default(1),
});

type ShadowComparisonSearch = z.infer<typeof shadowComparisonSearchSchema>;

export const Route = createFileRoute('/_app/admin/voice-qa/shadow-comparison')({
    head: () => ({
        meta: [
            {
                title: 'Voice QA Shadow Comparison | Phoebe Admin',
            },
        ],
    }),
    validateSearch: shadowComparisonSearchSchema,
    component: ShadowComparisonPage,
});

const compactBadgeClassName = 'h-6 px-2 text-xs leading-none';

const VERDICT_BADGES: Record<
    'perfect' | 'good' | 'bad' | 'none',
    { readonly label: string; readonly className: string }
> = {
    ...VOICE_QA_VERDICT_BADGES,
    none: { label: 'None', className: 'text-muted-foreground' },
};

function verdictLabel(verdict: RatingLabel | null | undefined): string {
    return VERDICT_BADGES[verdict ?? 'none'].label;
}

function VerdictBadge({
    verdict,
}: {
    readonly verdict: RatingLabel | null | undefined;
}) {
    const { label, className } = VERDICT_BADGES[verdict ?? 'none'];
    return (
        <Badge
            variant="secondary"
            className={cn(compactBadgeClassName, className)}
        >
            {label}
        </Badge>
    );
}

function formatAgreementRate(rate: number | null | undefined): string {
    return rate === null || rate === undefined
        ? '—'
        : `${(rate * 100).toFixed(1)}%`;
}

function shortModelName(model: string): string {
    const parts = model.split('/');
    return parts[parts.length - 1] || model;
}

function shadowLaneLabel(
    model: string,
    reasoningEffort: string | null | undefined,
): string {
    return reasoningEffort ? `${model} (${reasoningEffort})` : model;
}

function shadowLaneOptionValue(
    model: string,
    reasoningEffort: string | null | undefined,
): string {
    return JSON.stringify([model, reasoningEffort ?? null]);
}

function formatLatency(latency: VoiceQAShadowLatencyRead): string {
    if (latency.count === 0) {
        return 'n=0';
    }
    const p50 = latency.p50_ms ?? null;
    const p90 = latency.p90_ms ?? null;
    return `n=${latency.count} p50=${p50 ?? '—'}ms p90=${p90 ?? '—'}ms`;
}

// Human label for the primary critic side, from the distinct provenance
// strings the API saw in the window (usually one, e.g. "gemini-3.6-flash
// (medium, flex, async)").
function primaryModelsLabel(
    primaryModels: ReadonlyArray<string> | null | undefined,
): string {
    if (!primaryModels || primaryModels.length === 0) {
        return 'primary';
    }
    return primaryModels.map(shortModelName).join(', ');
}

function AgreementBadge({ pair }: { readonly pair: VoiceQAShadowPairRead }) {
    const { label, className } =
        pair.shadow.status === 'failed'
            ? { label: 'Shadow failed', className: 'text-amber-700' }
            : pair.verdicts_agree === true
              ? { label: 'Agree', className: 'text-green-700' }
              : pair.verdicts_agree === false
                ? { label: 'Disagree', className: 'text-red-700' }
                : { label: 'No verdict', className: 'text-muted-foreground' };
    return (
        <Badge
            variant="secondary"
            className={cn(compactBadgeClassName, className)}
        >
            {label}
        </Badge>
    );
}

function AdjudicationBadge({ pair }: { pair: VoiceQAShadowPairRead }) {
    const whoWasRight = pair.adjudication?.who_was_right ?? null;
    if (whoWasRight === null || whoWasRight === undefined) {
        return <span className="text-muted-foreground">—</span>;
    }
    return (
        <Badge
            variant="secondary"
            className={cn(
                compactBadgeClassName,
                adjudicationBadgeClassName(whoWasRight),
            )}
            title={pair.adjudication?.rationale ?? undefined}
        >
            {adjudicationLabel(whoWasRight)}
        </Badge>
    );
}

function formatAdjudicationCounts(
    counts: ReadonlyArray<VoiceQAShadowAdjudicationCountRead>,
): string | null {
    if (counts.length === 0) {
        return null;
    }
    return counts
        .map(
            (entry) =>
                `${adjudicationLabel(entry.who_was_right)} ${entry.count}`,
        )
        .join(' · ');
}

// Window-wide cost caveat: coverage is partial when fewer runs were costed
// than exist. Surface the counts so avg/total are read in context.
function CostLine({ stats }: { stats: VoiceQAShadowStatsResponse }) {
    const { shadow_cost, primary_cost } = stats;
    const partialCoverage =
        shadow_cost.costed_run_count < stats.completed_shadow_runs;
    return (
        <div className="space-y-1 text-xs">
            <p className="text-muted-foreground">Cost</p>
            <p>
                <span className="font-medium">Shadow (all lanes):</span>{' '}
                {costSummary(shadow_cost)}
            </p>
            <p>
                <span className="font-medium">
                    Primary ({primaryModelsLabel(stats.primary_models)}):
                </span>{' '}
                {costSummary(primary_cost)}
            </p>
            {partialCoverage ? (
                <p className="text-muted-foreground">
                    Costed {shadow_cost.costed_run_count} of{' '}
                    {stats.completed_shadow_runs} completed shadow runs.
                </p>
            ) : null}
        </div>
    );
}

function StatsHeader({ stats }: { stats: VoiceQAShadowStatsResponse }) {
    const adjudicationBreakdown = formatAdjudicationCounts(
        stats.adjudication_counts ?? [],
    );
    const agreementRate = formatAgreementRate(stats.agreement_rate);
    return (
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
            <Card className="min-w-0 gap-2 py-2 shadow-none">
                <CardHeader className="min-w-0 pb-0">
                    <CardTitle className="text-base">Summary</CardTitle>
                </CardHeader>
                <CardContent className="min-w-0 space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                        <SummaryStat
                            label="Pairs"
                            value={stats.paired_count.toLocaleString()}
                        />
                        <SummaryStat
                            label="Agreement"
                            value={`${agreementRate} (${stats.agreeing_pairs}/${stats.comparable_pairs})`}
                        />
                        <SummaryStat
                            label="Shadow runs"
                            value={stats.total_shadow_runs.toLocaleString()}
                        />
                        <SummaryStat
                            label="Completed"
                            value={stats.completed_shadow_runs.toLocaleString()}
                        />
                        <SummaryStat
                            label="Failed shadows"
                            value={stats.failed_shadow_runs.toLocaleString()}
                        />
                        <SummaryStat
                            label="Adjudicated"
                            value={
                                adjudicationBreakdown
                                    ? `${stats.adjudicated_count.toLocaleString()} (${adjudicationBreakdown})`
                                    : stats.adjudicated_count.toLocaleString()
                            }
                        />
                    </div>
                    <div className="space-y-1 text-xs">
                        <p className="text-muted-foreground">Latency</p>
                        <p>
                            <span className="font-medium">
                                Shadow (all lanes):
                            </span>{' '}
                            {formatLatency(stats.shadow_latency)}
                        </p>
                        <p>
                            <span className="font-medium">
                                Primary (
                                {primaryModelsLabel(stats.primary_models)}):
                            </span>{' '}
                            {formatLatency(stats.primary_critic_latency)}
                        </p>
                    </div>
                    <CostLine stats={stats} />
                </CardContent>
            </Card>
            <Card className="min-w-0 gap-2 py-2 shadow-none">
                <CardHeader className="min-w-0 pb-0">
                    <CardTitle className="text-base">
                        Verdict confusion matrix
                    </CardTitle>
                </CardHeader>
                <CardContent className="min-w-0">
                    <ConfusionMatrix
                        confusionMatrix={stats.confusion_matrix ?? []}
                    />
                </CardContent>
            </Card>
        </div>
    );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
    return (
        <div className="min-w-0 space-y-0.5">
            <p className="text-muted-foreground text-xs leading-tight">
                {label}
            </p>
            <p className="text-sm leading-tight font-medium [overflow-wrap:anywhere]">
                {value}
            </p>
        </div>
    );
}

const CONFUSION_VERDICTS: Array<RatingLabel | null> = [
    null,
    'perfect',
    'good',
    'bad',
];

function ConfusionMatrix({
    confusionMatrix,
}: {
    confusionMatrix: ReadonlyArray<VoiceQAShadowConfusionCell>;
}) {
    const countFor = (
        primary: RatingLabel | null,
        shadow: RatingLabel | null,
    ): number => {
        const cell = confusionMatrix.find(
            (entry) =>
                (entry.primary_verdict ?? null) === primary &&
                (entry.shadow_verdict ?? null) === shadow,
        );
        return cell?.count ?? 0;
    };
    return (
        <table className="w-full border-collapse text-xs">
            <thead>
                <tr>
                    <th className="text-muted-foreground p-1 text-left font-medium">
                        primary \ shadow
                    </th>
                    {CONFUSION_VERDICTS.map((shadow) => (
                        <th
                            key={verdictLabel(shadow)}
                            className="text-muted-foreground p-1 text-right font-medium"
                        >
                            {verdictLabel(shadow)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {CONFUSION_VERDICTS.map((primary) => (
                    <tr
                        key={verdictLabel(primary)}
                        className="border-border border-t"
                    >
                        <td className="text-muted-foreground p-1 font-medium">
                            {verdictLabel(primary)}
                        </td>
                        {CONFUSION_VERDICTS.map((shadow) => (
                            <td
                                key={verdictLabel(shadow)}
                                className="p-1 text-right tabular-nums"
                            >
                                {countFor(primary, shadow)}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function latencyPair(latency: VoiceQAShadowLatencyRead): string {
    return `p50 ${formatLatencyMs(latency.p50_ms)} · p90 ${formatLatencyMs(
        latency.p90_ms,
    )}`;
}

function ModelComparisonCard({
    model,
}: {
    model: VoiceQAShadowModelStatsRead;
}) {
    const agreementRate = formatAgreementRate(model.agreement_rate);
    const adjudicationBreakdown = formatAdjudicationCounts(
        model.adjudication_counts ?? [],
    );
    const laneLabel = shadowLaneLabel(
        shortModelName(model.model),
        model.reasoning_effort,
    );
    const primaryLabel = primaryModelsLabel(model.primary_models);
    return (
        <Card className="min-w-0 gap-2 py-2 shadow-none">
            <CardHeader className="min-w-0 pb-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-base">
                        {shadowLaneLabel(
                            shortModelName(model.model),
                            model.reasoning_effort,
                        )}
                    </CardTitle>
                    <Badge variant="outline" className={compactBadgeClassName}>
                        {model.completed_shadow_runs}/{model.total_shadow_runs}{' '}
                        completed
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="min-w-0 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                    <SummaryStat
                        label="Agreement"
                        value={`${agreementRate} (${model.agreeing_pairs}/${model.comparable_pairs})`}
                    />
                    <SummaryStat
                        label="Disagreements"
                        value={model.disagreement_count.toLocaleString()}
                    />
                </div>
                <div className="grid gap-2 text-xs sm:grid-cols-2">
                    <div className="space-y-0.5">
                        <p className="text-muted-foreground">
                            Latency — {laneLabel}
                        </p>
                        <p>{latencyPair(model.shadow_latency)}</p>
                    </div>
                    <div className="space-y-0.5">
                        <p className="text-muted-foreground">
                            Latency — {primaryLabel}
                        </p>
                        <p>{latencyPair(model.primary_critic_latency)}</p>
                    </div>
                    <div className="space-y-0.5">
                        <p className="text-muted-foreground">
                            Cost — {laneLabel}
                        </p>
                        <p>{costSummary(model.cost)}</p>
                    </div>
                    <div className="space-y-0.5">
                        <p className="text-muted-foreground">
                            Cost — {primaryLabel}
                        </p>
                        <p>{costSummary(model.primary_cost)}</p>
                    </div>
                </div>
                {adjudicationBreakdown ? (
                    <div className="space-y-0.5 text-xs">
                        <p className="text-muted-foreground">Adjudication</p>
                        <p>{adjudicationBreakdown}</p>
                    </div>
                ) : null}
                <details>
                    <summary className="text-muted-foreground cursor-pointer text-xs">
                        Confusion matrix
                    </summary>
                    <div className="mt-1.5">
                        <ConfusionMatrix
                            confusionMatrix={model.confusion_matrix ?? []}
                        />
                    </div>
                </details>
            </CardContent>
        </Card>
    );
}

function costSummary(cost: VoiceQAShadowCostRead): string {
    return `avg ${formatUsd(cost.avg_cost_usd)} · total ${formatUsd(
        cost.total_cost_usd,
    )}`;
}

function ShadowComparisonPage() {
    const router = useRouter();
    const search = Route.useSearch();
    const sinceDays = search.since_days;
    const disagreementsOnly = search.disagreements_only;
    const shadowModel = search.shadow_model;
    const shadowReasoningEffort = search.shadow_reasoning_effort;
    const page = search.page;

    const { enabled: uiEnabled } = useShadowComparisonUiEnabled();

    const {
        data: stats,
        isLoading: isStatsLoading,
        error: statsError,
    } = useVoiceQAShadowStats(
        { since_days: sinceDays },
        { enabled: uiEnabled === true },
    );
    const {
        data: comparison,
        isLoading: isComparisonLoading,
        error: comparisonError,
    } = useVoiceQAShadowComparison(
        {
            since_days: sinceDays,
            disagreements_only: disagreementsOnly,
            shadow_model: shadowModel,
            shadow_reasoning_effort: shadowReasoningEffort,
            page,
            page_size: PAGE_SIZE,
        },
        { enabled: uiEnabled === true },
    );

    const items = comparison?.items ?? [];
    const total = comparison?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const models = stats?.models ?? [];
    const laneOptions = models.map((model) => ({
        value: shadowLaneOptionValue(model.model, model.reasoning_effort),
        model: model.model,
        reasoningEffort: model.reasoning_effort ?? null,
        label: shadowLaneLabel(model.model, model.reasoning_effort),
    }));
    const selectedLaneValue = shadowModel
        ? shadowLaneOptionValue(shadowModel, shadowReasoningEffort)
        : ALL_MODELS_VALUE;

    const updateSearch = (next: Partial<ShadowComparisonSearch>) => {
        void router.navigate({
            to: '/admin/voice-qa/shadow-comparison',
            search: { ...search, ...next },
        });
    };

    // A stale bookmark or a narrowed filter can leave the URL page past
    // the last page; snap back to the last page once results load.
    useEffect(() => {
        if (comparison && page > totalPages) {
            void router.navigate({
                to: '/admin/voice-qa/shadow-comparison',
                search: { ...search, page: totalPages },
            });
        }
    }, [comparison, page, totalPages, router, search]);

    return (
        <div className="flex h-full flex-col">
            <div className="border-border flex items-center justify-between border-b px-3 py-2">
                <h1 className="text-base leading-6 font-semibold">
                    Voice QA Shadow Comparison
                </h1>
                <Link
                    to="/admin/voice-qa"
                    className="text-sm font-medium underline underline-offset-2"
                >
                    Back to Voice QA
                </Link>
            </div>
            {uiEnabled === null ? (
                <div className="text-muted-foreground p-3 text-sm">
                    Loading…
                </div>
            ) : uiEnabled === false ? (
                <div className="text-muted-foreground p-6 text-sm">
                    Shadow comparison is disabled. Enable the dev flag in Voice
                    QA settings.
                </div>
            ) : (
                <Fragment>
                    <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2">
                        <div className="flex items-center gap-1">
                            {SINCE_DAYS_OPTIONS.map((option) => (
                                <Button
                                    key={option}
                                    variant={
                                        option === sinceDays
                                            ? 'default'
                                            : 'outline'
                                    }
                                    size="sm"
                                    onClick={() =>
                                        updateSearch({
                                            since_days: option,
                                            page: 1,
                                        })
                                    }
                                >
                                    {option}d
                                </Button>
                            ))}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <Select
                                value={selectedLaneValue}
                                onValueChange={(value) => {
                                    if (value === ALL_MODELS_VALUE) {
                                        updateSearch({
                                            shadow_model: undefined,
                                            shadow_reasoning_effort: undefined,
                                            page: 1,
                                        });
                                        return;
                                    }
                                    const lane = laneOptions.find(
                                        (option) => option.value === value,
                                    );
                                    if (!lane) {
                                        return;
                                    }
                                    updateSearch({
                                        shadow_model: lane.model,
                                        shadow_reasoning_effort:
                                            lane.reasoningEffort ?? undefined,
                                        page: 1,
                                    });
                                }}
                            >
                                <SelectTrigger
                                    size="sm"
                                    className="w-80 max-w-full"
                                >
                                    <SelectValue placeholder="All models" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={ALL_MODELS_VALUE}>
                                        All models
                                    </SelectItem>
                                    {laneOptions.map((option) => (
                                        <SelectItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Button
                                variant={
                                    disagreementsOnly ? 'default' : 'outline'
                                }
                                size="sm"
                                onClick={() =>
                                    updateSearch({
                                        disagreements_only: !disagreementsOnly,
                                        page: 1,
                                    })
                                }
                            >
                                Disagreements only
                            </Button>
                        </div>
                    </div>
                    <div className="min-w-0 space-y-3 overflow-auto p-3">
                        {statsError ? (
                            <div className="text-destructive text-sm">
                                Failed to load shadow stats. Try reloading the
                                page.
                            </div>
                        ) : isStatsLoading || !stats ? (
                            <div className="text-muted-foreground text-sm">
                                Loading stats…
                            </div>
                        ) : (
                            <Fragment>
                                <StatsHeader stats={stats} />
                                {models.length > 0 ? (
                                    <div className="grid gap-3 lg:grid-cols-2">
                                        {models.map((model) => (
                                            <ModelComparisonCard
                                                key={shadowLaneOptionValue(
                                                    model.model,
                                                    model.reasoning_effort,
                                                )}
                                                model={model}
                                            />
                                        ))}
                                    </div>
                                ) : null}
                            </Fragment>
                        )}
                        <div className="border-border flex items-center justify-between border-b pb-2">
                            <span className="text-muted-foreground text-sm">
                                {total.toLocaleString()} pairs
                            </span>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        updateSearch({ page: page - 1 })
                                    }
                                    disabled={page <= 1}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <span className="text-sm">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() =>
                                        updateSearch({ page: page + 1 })
                                    }
                                    disabled={page >= totalPages}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                        {comparisonError ? (
                            <div className="text-destructive text-sm">
                                Failed to load shadow pairs. Try reloading the
                                page.
                            </div>
                        ) : isComparisonLoading && items.length === 0 ? (
                            <div className="text-muted-foreground text-sm">
                                Loading pairs…
                            </div>
                        ) : items.length === 0 ? (
                            <div className="text-muted-foreground text-sm">
                                No shadow pairs found in this window.
                            </div>
                        ) : (
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="text-muted-foreground border-border border-b text-left text-xs">
                                        <th className="p-2 font-medium">
                                            When
                                        </th>
                                        <th className="p-2 font-medium">
                                            Organization
                                        </th>
                                        <th className="p-2 font-medium">
                                            Model (shadow / primary)
                                        </th>
                                        <th className="p-2 font-medium">
                                            Verdicts
                                        </th>
                                        <th className="p-2 font-medium">
                                            Agreement
                                        </th>
                                        <th className="p-2 font-medium">
                                            Adjudication
                                        </th>
                                        <th className="p-2 font-medium">
                                            Latency
                                        </th>
                                        <th className="p-2 font-medium">
                                            Cost
                                        </th>
                                        <th className="p-2 font-medium">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((pair) => (
                                        <ShadowPairRow
                                            key={pair.shadow.run_id}
                                            pair={pair}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </Fragment>
            )}
        </div>
    );
}

function ShadowPairRow({ pair }: { pair: VoiceQAShadowPairRead }) {
    const simulation = pair.simulation ?? null;
    return (
        <tr className="border-border hover:bg-muted/40 border-b">
            <td className="p-2 align-top">
                <Link
                    to="/admin/voice-qa/$callId"
                    params={{ callId: pair.voice_call_id }}
                    className="whitespace-nowrap underline underline-offset-2"
                >
                    {formatDistanceToNow(new Date(pair.created_at), {
                        addSuffix: true,
                    })}
                </Link>
            </td>
            <td className="p-2 align-top [overflow-wrap:anywhere]">
                {pair.organization_name}
            </td>
            {/* Shadow on the first line, primary on the second — every
                stacked cell in the row follows this order. */}
            <td className="text-muted-foreground p-2 align-top text-xs [overflow-wrap:anywhere]">
                <p>{shortModelName(pair.shadow.model)}</p>
                <p>{shortModelName(pair.primary.model)}</p>
            </td>
            <td className="p-2 align-top">
                <div className="flex flex-col gap-1">
                    <VerdictBadge verdict={pair.shadow.verdict} />
                    <VerdictBadge verdict={pair.primary.verdict} />
                </div>
            </td>
            <td className="p-2 align-top">
                <AgreementBadge pair={pair} />
            </td>
            <td className="p-2 align-top">
                <AdjudicationBadge pair={pair} />
            </td>
            <td className="text-muted-foreground p-2 align-top text-xs whitespace-nowrap tabular-nums">
                <p>{formatLatencyMs(pair.shadow.latency_ms)}</p>
                <p>{formatLatencyMs(pair.primary.critic_latency_ms)}</p>
            </td>
            <td className="text-muted-foreground p-2 align-top text-xs whitespace-nowrap tabular-nums">
                <p>{formatUsd(pair.shadow.estimated_cost_usd)}</p>
                <p>{formatUsd(pair.primary.estimated_cost_usd)}</p>
            </td>
            <td className="text-muted-foreground p-2 align-top text-xs">
                {simulation ? (
                    <Fragment>
                        sim {simulation.would_execute_count} exec /{' '}
                        {simulation.blocked_count} blocked
                    </Fragment>
                ) : (
                    <span>sim —</span>
                )}
                <p>
                    prod {pair.primary.executed_count} exec /{' '}
                    {pair.primary.blocked_or_skipped_count} blocked
                </p>
            </td>
        </tr>
    );
}
